<template>
  <div class="random">
    <div class="container">
      <header class="random__header">
        <p class="eyebrow reveal">Pioche du jour</p>
        <h1 class="random__title reveal">
          Le hasard fait<br />bien les <span class="random__word">blagues<span class="random__peel">🍌</span></span>
        </h1>
        <p class="random__subtitle reveal">
          Une perle tirée au sort dans le grand régime.<br />
          Pas convaincu&nbsp;? On en repioche une.
        </p>
      </header>

      <!-- Filters -->
      <div class="filters reveal">
        <!-- Score filter -->
        <div class="filter-group">
          <p class="filter-group__label">Quel genre de perle&nbsp;?</p>
          <div class="filter filter--score">
            <button
              v-for="opt in scoreOptions"
              :key="opt.value"
              class="filter__pill"
              :class="{ 'filter__pill--active': scoreFilter === opt.value }"
              @click="selectScore(opt.value)"
            >
              <span class="filter__emoji">{{ opt.emoji }}</span>{{ opt.label }}
            </button>
          </div>
        </div>

        <!-- Tag filter -->
        <div v-if="tagGroups.length" class="filter-group">
          <div class="filter-group__head">
            <p class="filter-group__label">Affine par tags <span class="filter-group__hint">— clique pour inclure, reclique pour exclure</span></p>
            <button
              v-if="activeTagCount"
              class="filter-group__clear"
              @click="clearTags"
            >
              ✕ Tout effacer ({{ activeTagCount }})
            </button>
          </div>
          <div v-for="group in tagGroups" :key="group.axis" class="tag-axis">
            <span class="tag-axis__title">{{ group.title }}</span>
            <div class="filter filter--tags">
              <button
                v-for="tag in group.tags"
                :key="tag.slug"
                class="filter__pill filter__pill--tag"
                :class="{
                  'filter__pill--include': tagState(tag.slug) === 'include',
                  'filter__pill--exclude': tagState(tag.slug) === 'exclude',
                  'filter__pill--sensitive': tag.isSensitive,
                }"
                :title="tagState(tag.slug) === 'include' ? 'Inclus — reclique pour exclure'
                  : tagState(tag.slug) === 'exclude' ? 'Exclu — reclique pour retirer'
                  : 'Clique pour inclure'"
                @click="toggleTag(tag.slug)"
              >
                <span v-if="tagState(tag.slug) === 'include'" class="filter__mark">✓</span>
                <span v-else-if="tagState(tag.slug) === 'exclude'" class="filter__mark">✕</span>{{ tag.label }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Joke stage -->
      <div class="stage" ref="stage">
        <transition name="flip" mode="out-in">
          <div v-if="loading" key="loading" class="stage__loading">
            <div class="dice">🎲</div>
            <p>On secoue le bananier…</p>
          </div>

          <div v-else-if="error" key="error" class="stage__empty">
            <div class="stage__empty-emoji">🍌💨</div>
            <p>Plus de banane sous la main…</p>
            <BananaButton variant="outline" size="md" @click="draw()">Réessayer</BananaButton>
          </div>

          <div v-else-if="!joke" key="empty" class="stage__empty">
            <div class="stage__empty-emoji">🍌🕳️</div>
            <p>Aucune banane ne correspond à ces filtres…<br />Essaie d'en relâcher un ou deux&nbsp;!</p>
            <BananaButton variant="outline" size="md" @click="resetFilters">Réinitialiser les filtres</BananaButton>
          </div>

          <div v-else :key="joke.id" class="stage__joke">
            <JokeCard :joke="joke" />
          </div>
        </transition>
      </div>

      <!-- Draw again -->
      <div class="random__actions">
        <BananaButton
          variant="dark"
          size="lg"
          :loading="loading"
          @click="draw"
        >
          🎲 Une autre&nbsp;!
        </BananaButton>
      </div>
    </div>
  </div>
</template>

<script>
import JokeCard from '@/components/JokeCard.vue'
import BananaButton from '@/components/ui/BananaButton.vue'
import { RANDOM_JOKE_QUERY, JOKE_BY_ID_QUERY, TAGS_QUERY } from '@/graphql/jokes'

// Human-friendly titles for the tag axes returned by the API.
const AXIS_TITLES = {
  ton: 'Le ton',
  theme: 'Le thème',
  thème: 'Le thème',
}
// Order in which axes appear (unknown axes fall to the end).
const AXIS_ORDER = ['ton', 'theme', 'thème']

export default {
  name: 'RandomView',
  components: { JokeCard, BananaButton },
  data() {
    return {
      joke: null,
      tags: [],
      // slug → 'include' | 'exclude' (absent = neutral). Tri-state like Search.
      tagStates: {},
      scoreFilter: 'all',
      loading: true,
      error: false,
      scoreOptions: [
        { value: 'all', label: 'Tout', emoji: '🎲' },
        { value: 'legacy_positive', label: 'Coup de cœur légende', emoji: '👑' },
        { value: 'current_positive', label: 'Populaire maintenant', emoji: '🔥' },
        { value: 'current_negative', label: 'Flop actuel', emoji: '💩' },
        { value: 'legacy_negative', label: 'Flop légende', emoji: '💀' },
      ],
    }
  },
  apollo: {
    tags: {
      query: TAGS_QUERY,
      update: (data) => data.tags || [],
    },
  },
  computed: {
    includeTags() {
      return Object.keys(this.tagStates).filter((s) => this.tagStates[s] === 'include')
    },
    excludeTags() {
      return Object.keys(this.tagStates).filter((s) => this.tagStates[s] === 'exclude')
    },
    activeTagCount() {
      return this.includeTags.length + this.excludeTags.length
    },
    // Tags grouped by axis, ordered ton → thème → rest.
    tagGroups() {
      const byAxis = {}
      for (const tag of this.tags) {
        const axis = tag.axis || 'autre'
        if (!byAxis[axis]) byAxis[axis] = []
        byAxis[axis].push(tag)
      }
      return Object.keys(byAxis)
        .sort((a, b) => {
          const ia = AXIS_ORDER.indexOf(a)
          const ib = AXIS_ORDER.indexOf(b)
          return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)
        })
        .map((axis) => ({
          axis,
          title: AXIS_TITLES[axis] || (axis.charAt(0).toUpperCase() + axis.slice(1)),
          tags: byAxis[axis],
        }))
    },
  },
  mounted() {
    // If we arrive with a specific joke pinned in the URL (?j=<id>) — e.g. after
    // logging in to vote — show THAT joke rather than drawing a new random one.
    const pinned = parseInt(this.$route.query.j, 10)
    if (pinned) this.showById(pinned)
    else this.draw()
  },
  methods: {
    selectScore(value) {
      if (this.scoreFilter === value) return
      this.scoreFilter = value
      this.draw()
    },
    // Cycle a tag: neutral → include → exclude → neutral (like Search).
    toggleTag(slug) {
      const cur = this.tagStates[slug]
      const next = { ...this.tagStates }
      if (!cur) next[slug] = 'include'
      else if (cur === 'include') next[slug] = 'exclude'
      else delete next[slug]
      this.tagStates = next
      this.draw()
    },
    tagState(slug) {
      return this.tagStates[slug] || null
    },
    clearTags() {
      if (!this.activeTagCount) return
      this.tagStates = {}
      this.draw()
    },
    resetFilters() {
      this.tagStates = {}
      this.scoreFilter = 'all'
      this.draw()
    },
    // Draw a random joke. Whatever the trigger (arrival, "une autre", a tag or
    // a filter change), scrollToStage() applies the single rule: if the viewport
    // sits below the top of the joke, pull back up to it; otherwise stay put.
    async draw() {
      this.loading = true
      this.error = false
      try {
        const { data } = await this.$apollo.query({
          query: RANDOM_JOKE_QUERY,
          variables: {
            tags: this.includeTags.length ? this.includeTags : null,
            excludeTags: this.excludeTags.length ? this.excludeTags : null,
            scoreFilter: this.scoreFilter,
          },
          fetchPolicy: 'network-only',
        })
        this.joke = data.randomJoke
        this.pinCurrent()
        this.scrollToStage()
      } catch (e) {
        this.error = true
        this.joke = null
      } finally {
        this.loading = false
      }
    },
    // On every new joke: if the viewport is BELOW the top of the joke, scroll up
    // to it — never down. So a long joke you'd scrolled through pulls you back to
    // the new one's start, while a joke already in view (fresh arrival, big
    // screen) doesn't move. The out-in transition means we wait a couple frames
    // for layout before measuring.
    scrollToStage() {
      // Deferred past the router's own scroll handling (the ?j= pin) with a
      // timeout, and re-run once more shortly after so the final scroll wins
      // even if the router or a re-render nudged the position back.
      const doScroll = () => {
        const el = this.$refs.stage
        if (!el) return
        const y = Math.max(0, window.scrollY + el.getBoundingClientRect().top - 80) // sticky nav offset
        // Only ever scroll up — never push the page further down.
        if (y < window.scrollY) window.scrollTo(0, y)
      }
      this.$nextTick(() => {
        setTimeout(doScroll, 0)
        setTimeout(doScroll, 250)
      })
    },
    // Load one specific joke by id (used when the URL pins ?j=<id>). Falls back
    // to a fresh random draw if that joke can't be loaded.
    async showById(id) {
      this.loading = true
      this.error = false
      try {
        const { data } = await this.$apollo.query({
          query: JOKE_BY_ID_QUERY,
          variables: { id },
          fetchPolicy: 'network-only',
        })
        if (data && data.joke) {
          this.joke = data.joke
        } else {
          await this.draw()
        }
      } catch (e) {
        await this.draw()
      } finally {
        this.loading = false
      }
    },
    // Reflect the current joke in the URL (?j=<id>) without adding a history
    // entry, so a round-trip through /login comes back to the SAME joke.
    // Returns the navigation promise so callers can scroll AFTER it settles.
    pinCurrent() {
      const id = this.joke && this.joke.id
      if (!id) return Promise.resolve()
      if (String(this.$route.query.j) === String(id)) return Promise.resolve()
      return this.$router
        .replace({ query: { ...this.$route.query, j: String(id) } })
        .catch(() => {})
    },
  },
}
</script>

<style scoped>
.random {
  padding: 48px 0 80px;
}

/* ---------- Header ---------- */
.random__header {
  text-align: center;
  margin-bottom: 32px;
}
.random__title {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: clamp(2.4rem, 6vw, 4rem);
  line-height: 0.98;
  letter-spacing: -0.03em;
  color: var(--ink-900);
  margin: 8px 0 18px;
}
.random__word {
  position: relative;
  display: inline-block;
  color: var(--banana-600);
}
.random__peel {
  display: inline-block;
  margin-left: 6px;
  animation: swing 2.6s ease-in-out infinite;
  transform-origin: top center;
}
@keyframes swing {
  0%, 100% { transform: rotate(-8deg); }
  50% { transform: rotate(10deg); }
}
.random__subtitle {
  font-family: var(--font-accent);
  font-style: italic;
  font-size: clamp(1rem, 2.4vw, 1.25rem);
  color: var(--ink-500);
  line-height: 1.5;
  margin: 0;
}

/* ---------- Filters layout ---------- */
.filters {
  display: flex;
  flex-direction: column;
  gap: 28px;
  margin-bottom: 44px;
}
.filter-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.filter-group__head {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}
.filter-group__label {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 1rem;
  color: var(--ink-900);
  text-align: center;
  margin: 0;
}
.filter-group__hint {
  font-family: var(--font-accent);
  font-style: italic;
  font-weight: 400;
  font-size: 0.9rem;
  color: var(--ink-500);
}
.filter-group__clear {
  font-family: var(--font-body);
  font-weight: 700;
  font-size: 0.8rem;
  color: var(--ink-700);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: var(--r-pill);
  transition: background 0.15s ease, color 0.15s ease;
}
.filter-group__clear:hover {
  background: var(--banana-100);
  color: var(--ink-900);
}

/* Tag axis rows */
.tag-axis {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.tag-axis__title {
  font-family: var(--font-accent);
  font-style: italic;
  font-size: 0.85rem;
  color: var(--ink-500);
  text-transform: lowercase;
  letter-spacing: 0.01em;
}

/* ---------- Filter pills ---------- */
.filter {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
}
.filter--tags {
  gap: 8px;
}
.filter__pill {
  --pill-color: var(--ink-900);
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-family: var(--font-body);
  font-weight: 700;
  font-size: 0.92rem;
  padding: 9px 16px;
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-pill);
  background: var(--cream);
  color: var(--ink-900);
  cursor: pointer;
  transition: transform 0.14s var(--ease-bounce), box-shadow 0.14s var(--ease-out),
    background 0.18s ease, color 0.18s ease;
  box-shadow: 2px 2px 0 var(--border-c);
}
.filter__emoji {
  font-size: 1.05rem;
  line-height: 1;
}
.filter__pill:hover {
  transform: translate(-1px, -1px);
  box-shadow: 4px 4px 0 var(--border-c);
}
.filter__pill:active {
  transform: translate(1px, 1px);
  box-shadow: 1px 1px 0 var(--border-c);
}
.filter__pill--active {
  background: var(--pill-color);
  color: var(--cream);
  box-shadow: 4px 4px 0 var(--border-c);
}

/* Tag pills: smaller + banana accent when active */
.filter__pill--tag {
  --pill-color: var(--banana-500);
  font-size: 0.85rem;
  padding: 7px 13px;
  box-shadow: 2px 2px 0 var(--border-c);
}
.filter__pill--tag.filter__pill--active {
  color: var(--ink-900);
}
.filter__pill--sensitive {
  --pill-color: var(--bruise);
}

/* Tri-state tag pills: included (green) vs excluded (red). */
.filter__pill--include {
  background: var(--peel-green);
  color: #fff;
  box-shadow: 4px 4px 0 var(--border-c);
}
.filter__pill--exclude {
  background: var(--bruise);
  color: #fff;
  box-shadow: 4px 4px 0 var(--border-c);
  text-decoration: line-through;
}
.filter__mark { font-weight: 800; margin-right: 4px; }

/* ---------- Stage ---------- */
.stage {
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 36px;
}
.stage__joke {
  width: 100%;
}

.stage__loading,
.stage__empty {
  text-align: center;
  padding: 40px 20px;
  color: var(--ink-500);
}
.stage__loading p {
  font-family: var(--font-accent);
  font-style: italic;
  font-size: 1.1rem;
  margin: 16px 0 0;
}
.dice {
  font-size: 3.4rem;
  display: inline-block;
  animation: roll 0.9s var(--ease-bounce) infinite;
}
@keyframes roll {
  0% { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(180deg) scale(1.15); }
  100% { transform: rotate(360deg) scale(1); }
}
.stage__empty-emoji {
  font-size: 3rem;
  margin-bottom: 12px;
}
.stage__empty p {
  font-family: var(--font-accent);
  font-style: italic;
  font-size: 1.15rem;
  margin: 0 0 20px;
  color: var(--ink-900);
}

/* ---------- Actions ---------- */
.random__actions {
  display: flex;
  justify-content: center;
}

/* ---------- Flip transition ---------- */
.flip-enter-active {
  transition: transform 0.4s var(--ease-bounce), opacity 0.3s ease;
}
.flip-leave-active {
  transition: transform 0.28s var(--ease-out), opacity 0.22s ease;
}
.flip-enter-from {
  opacity: 0;
  transform: translateY(22px) rotate(-1.5deg) scale(0.96);
}
.flip-leave-to {
  opacity: 0;
  transform: translateY(-14px) scale(0.97);
}

@media (max-width: 560px) {
  .random { padding: 32px 0 60px; }
  .filters { gap: 22px; margin-bottom: 34px; }
  .filter { gap: 8px; }
  .filter__pill { font-size: 0.85rem; padding: 8px 13px; }
  .filter__pill--tag { font-size: 0.8rem; padding: 6px 11px; }
}
</style>
